﻿import { useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  EmptyState,
  InlineStack,
  Layout,
  Page,
  Select,
  Spinner,
  Text,
} from '@shopify/polaris';
import { ChartHistogramGrowthIcon } from '@shopify/polaris-icons';
import type { AbTestSuggestion, TestStatus, TestType } from '../../api/abTests';
import {
  useAbTests,
  useDeleteAbTest,
  useGenerateAbTests,
  useUpdateAbTestStatus,
} from '../../hooks/useAbTests';
import { IconBadge } from '../shared/IconBadge';

const PRIORITY_TONES: Record<string, 'success' | 'warning' | 'attention'> = {
  high:   'success',
  medium: 'warning',
  low:    'attention',
};

const STATUS_TONES: Record<string, 'info' | 'success' | 'attention' | 'warning'> = {
  new:       'info',
  running:   'success',
  completed: 'attention',
  dismissed: 'warning',
};

function SuggestionCard({
  suggestion,
  onSelect,
}: {
  suggestion: AbTestSuggestion;
  onSelect: (s: AbTestSuggestion) => void;
}) {
  const updateStatus = useUpdateAbTestStatus();
  const deleteTest   = useDeleteAbTest();

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="start" wrap={false} gap="200">
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <IconBadge icon={ChartHistogramGrowthIcon} color={suggestion.priority === 'high' ? 'green' : 'indigo'} />
            <BlockStack gap="100">
              <InlineStack gap="200">
                <Badge tone={PRIORITY_TONES[suggestion.priority] ?? 'info'}>
                  {suggestion.priority_label}
                </Badge>
                <Badge tone="info">{suggestion.test_type_label}</Badge>
                <Badge tone={STATUS_TONES[suggestion.status] ?? 'info'}>
                  {suggestion.status_label}
                </Badge>
              </InlineStack>
              <Text variant="headingMd" as="h3">{suggestion.page_or_element}</Text>
            </BlockStack>
          </InlineStack>
          {suggestion.estimated_lift_pct !== null && (
            <Text variant="bodyMd" fontWeight="bold" tone="success" as="span">
              +{suggestion.estimated_lift_pct}% est.
            </Text>
          )}
        </InlineStack>

        <Text variant="bodySm" tone="subdued" as="p">{suggestion.hypothesis}</Text>

        <InlineStack gap="200" wrap>
          <Button size="slim" onClick={() => onSelect(suggestion)}>View details</Button>
          {suggestion.status === 'new' && (
            <Button
              size="slim"
              onClick={() => updateStatus.mutate({ id: suggestion.id, status: 'running' })}
              loading={updateStatus.isPending}
            >
              Mark running
            </Button>
          )}
          {suggestion.status === 'running' && (
            <Button
              size="slim"
              onClick={() => updateStatus.mutate({ id: suggestion.id, status: 'completed' })}
              loading={updateStatus.isPending}
            >
              Mark completed
            </Button>
          )}
          {suggestion.is_active && (
            <Button
              size="slim"
              tone="critical"
              onClick={() => updateStatus.mutate({ id: suggestion.id, status: 'dismissed' })}
            >
              Dismiss
            </Button>
          )}
          <Button
            size="slim"
            tone="critical"
            onClick={() => deleteTest.mutate(suggestion.id)}
          >
            Delete
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

function SuggestionDetail({
  suggestion,
  onBack,
}: {
  suggestion: AbTestSuggestion;
  onBack: () => void;
}) {
  const updateStatus = useUpdateAbTestStatus();

  return (
    <BlockStack gap="500">
      <Button onClick={onBack} variant="plain">← Back to list</Button>

      <Card>
        <BlockStack gap="400">
          <InlineStack gap="200">
            <Badge tone={PRIORITY_TONES[suggestion.priority] ?? 'info'}>
              {suggestion.priority_label}
            </Badge>
            <Badge tone="info">{suggestion.test_type_label}</Badge>
            <Badge tone={STATUS_TONES[suggestion.status] ?? 'info'}>
              {suggestion.status_label}
            </Badge>
          </InlineStack>

          <BlockStack gap="100">
            <Text variant="headingLg" as="h2">{suggestion.page_or_element}</Text>
            {suggestion.estimated_lift_pct !== null && (
              <Text variant="bodyMd" tone="success" as="p">
                Estimated lift: +{suggestion.estimated_lift_pct}%
              </Text>
            )}
          </BlockStack>

          <BlockStack gap="100">
            <Text variant="headingSm" as="h4">Hypothesis</Text>
            <Text variant="bodyMd" as="p">{suggestion.hypothesis}</Text>
          </BlockStack>

          <BlockStack gap="100">
            <Text variant="headingSm" as="h4">Expected impact</Text>
            <Text variant="bodyMd" as="p">{suggestion.expected_impact}</Text>
          </BlockStack>
        </BlockStack>
      </Card>

      <Layout>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="200">
              <Text variant="headingSm" as="h4">Control (current)</Text>
              <Text variant="bodyMd" as="p">{suggestion.control_description}</Text>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="200">
              <Text variant="headingSm" as="h4" tone="success">Variant (test)</Text>
              <Text variant="bodyMd" as="p">{suggestion.variant_description}</Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {suggestion.is_active && (
        <InlineStack gap="200">
          {suggestion.status === 'new' && (
            <Button
              variant="primary"
              onClick={() => updateStatus.mutate({ id: suggestion.id, status: 'running' })}
            >
              Start running this test
            </Button>
          )}
          {suggestion.status === 'running' && (
            <Button
              onClick={() => updateStatus.mutate({ id: suggestion.id, status: 'completed' })}
            >
              Mark as completed
            </Button>
          )}
          <Button
            tone="critical"
            onClick={() => updateStatus.mutate({ id: suggestion.id, status: 'dismissed' })}
          >
            Dismiss
          </Button>
        </InlineStack>
      )}
    </BlockStack>
  );
}

function GenerateButton() {
  const generate = useGenerateAbTests();
  const [error, setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleGenerate = async () => {
    setError(null);
    setSuccess(null);
    try {
      const result = await generate.mutateAsync(undefined);
      setSuccess(`Generated ${result.suggestion_count} A/B test ideas.`);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to generate tests. Run a store audit first.');
    }
  };

  return (
    <BlockStack gap="300">
      {error   && <Banner tone="critical">{error}</Banner>}
      {success && <Banner tone="success">{success}</Banner>}
      <Button
        variant="primary"
        onClick={handleGenerate}
        loading={generate.isPending}
      >
        Generate A/B test ideas
      </Button>
    </BlockStack>
  );
}

export function AbTestPanel() {
  const [selected, setSelected] = useState<AbTestSuggestion | null>(null);
  const [statusFilter, setStatusFilter] = useState<TestStatus | undefined>(undefined);
  const [typeFilter, setTypeFilter]     = useState<TestType | undefined>(undefined);
  const [page, setPage]                 = useState(1);

  const { data, isLoading, error } = useAbTests(
    { status: statusFilter, test_type: typeFilter },
    page,
  );

  const suggestions = data?.data ?? [];
  const meta        = data?.meta;

  if (selected) {
    return (
      <Page fullWidth title="A/B Test Details">
        <Layout>
          <Layout.Section>
            <SuggestionDetail suggestion={selected} onBack={() => setSelected(null)} />
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page fullWidth
      title="A/B Test Ideas"
      subtitle="AI-generated split test suggestions based on your store audit results"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            <Card>
              <GenerateButton />
            </Card>

            <InlineStack gap="200">
              <Select
                label="Status"
                labelHidden
                options={[
                  { label: 'All statuses', value: '' },
                  { label: 'New', value: 'new' },
                  { label: 'Running', value: 'running' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Dismissed', value: 'dismissed' },
                ]}
                value={statusFilter ?? ''}
                onChange={(v) => { setStatusFilter(v as TestStatus || undefined); setPage(1); }}
              />
              <Select
                label="Type"
                labelHidden
                options={[
                  { label: 'All types', value: '' },
                  { label: 'Copy & Messaging', value: 'copy' },
                  { label: 'Design & Layout', value: 'design' },
                  { label: 'Pricing & Offers', value: 'pricing' },
                  { label: 'User Flow', value: 'flow' },
                  { label: 'Trust & Social Proof', value: 'trust' },
                  { label: 'SEO', value: 'seo' },
                ]}
                value={typeFilter ?? ''}
                onChange={(v) => { setTypeFilter(v as TestType || undefined); setPage(1); }}
              />
            </InlineStack>

            {isLoading && <Spinner />}
            {error    && <Banner tone="critical">Failed to load A/B test suggestions.</Banner>}

            {!isLoading && !error && suggestions.length === 0 && (
              <EmptyState
                heading="No A/B test ideas yet"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Click "Generate A/B test ideas" above to get AI-powered split test suggestions based on your latest store audit.</p>
              </EmptyState>
            )}

            {suggestions.map((s) => (
              <SuggestionCard key={s.id} suggestion={s} onSelect={setSelected} />
            ))}

            {meta && meta.last_page > 1 && (
              <InlineStack gap="200" align="center">
                <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Text as="span" variant="bodySm">{page} / {meta.last_page}</Text>
                <Button disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </InlineStack>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
